import React, { useEffect, useState } from "react";
import "./style.css";
import { PopUpModal, TextInput, Title } from "../../Components/Atoms";
import { Button, IconButton } from "@chakra-ui/react";
import { CircledCheckIcon, showPwdIcon as ShowPassword, hidePwdIcon as HidePassword, TickIconGreen } from "../../Assets/images/svgs";
import { sendOtpForPasswordUpdate, verifyOtpAndUpdatePassword } from "../../APIs";
import { isValidMobile } from "../../Library/validations";
import { useNavigate } from "react-router-dom";
import PATHS from "../../Routes/paths";

const ChangePassword = () => {
  const navigate = useNavigate();
  const [registeredNumber, setRegisteredNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [isOtpRequested, setIsOtpRequested] = useState(false);
  const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);
  const [showPassword, setShowPassword] = useState({ newPassword: false, confirmPassword: false });
  const [arePasswordsSame, setArePasswordsSame] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [counter, setCounter] = useState(60);

  const [errorMessages, setErrorMessages] = useState({
    registeredNumber: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
    generalError: "",
  });

  const startOtpCountdown = () => {
    let interval = setInterval(() => {
      setCounter((prev) => {
        if (prev === 0) clearInterval(interval);
        return prev - 1;
      });
    }, 1000);
  }

  const handleGetOtp = () => {
    if (!registeredNumber || !isValidMobile(registeredNumber)) {
      setErrorMessages({
        registeredNumber: "Please provide a valid registered number.",
      });
      return;
    }

    const payload = { mob: parseInt(registeredNumber, 10) };
    sendOtpForPasswordUpdate(payload, (err) => {
      if (err) {
        let errMsg = "";
        if (err && err.time_left) errMsg = `Please request OTP again later.`
        else errMsg = err;

        console.error("Error Requesting OTP.");
        setErrorMessages({ generalError: errMsg });
        return;
      } else {
        setCounter(60);
        setIsOtpRequested(true);
        setShowSuccessModal(true);
        startOtpCountdown();
        setErrorMessages({});
        setTimeout(() => {
          setShowSuccessModal(false);
        }, 2000);
      }
    });
  };

  const handleVerifyOtpAndUpdatePassword = () => {
    if (!otp || !registeredNumber || !newPassword || !confirmPassword) return;

    if (otp.length !== 6) return setErrorMessages({ otp: "The OTP must be exactly 6 digits." });

    if (newPassword.length < 6 || newPassword.length > 15) return setErrorMessages({ newPassword: "Password length should be between 6 and 15 characters." });

    if (newPassword !== confirmPassword) return setErrorMessages({ confirmPassword: "The new password and confirmation do not match." });

    const payload = {
      mob: parseInt(registeredNumber, 10),
      otp: otp,
      newPwd: newPassword,
    };
    verifyOtpAndUpdatePassword(payload, (err) => {
      if (err) {
        console.error("Error Verifying OTP.");
        setErrorMessages({ generalError: err });
        return;
      } else {
        setIsPasswordUpdated(true);
        setShowSuccessModal(true);

        setTimeout(() => {
          setShowSuccessModal(false);
          navigate(PATHS.SETTINGS);
        }, 2000);
      }
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({...prev, [field]: !prev[field]}));
  };

  const getSuccessModalBody = () => {
    return (
      <div>
        <div className="change-password-modal">
          <CircledCheckIcon />
          <p>
            {isOtpRequested && !isPasswordUpdated ? "OTP has been sent to your registered number." : "Your password has been successfully updated."}
          </p>
        </div>
      </div>
    );
  };

  useEffect(() => {
    setArePasswordsSame(newPassword && confirmPassword && newPassword === confirmPassword);
  }, [confirmPassword, newPassword]);

  return (
    <div className="change-password-wrapper">
      <div className="title-cnt">
        <Title>Change Password</Title>
      </div>
      <div className="form-cnt">
        {!isOtpRequested && (
          <div className="form-title-cnt">
            <span className="form-title">Verify Your Registered Number</span>
          </div>
        )}

        <div className="form-fields">
          {!isOtpRequested ? (
            <TextInput
              inputName="registeredNo"
              placeholder="Enter registered number"
              className="form-input"
              label="Registered Number"
              labelClassName="form-input-label"
              value={registeredNumber}
              onChange={(str) => setRegisteredNumber(str)}
              errMessage={errorMessages.registeredNumber}
              autoFocus={true}
              type="number"
            />
          ) : (
            <>
              <TextInput
                inputName="otp"
                placeholder="Enter OTP"
                className="form-input"
                label="OTP"
                labelClassName="form-input-label"
                value={otp}
                onChange={(str) => setOtp(str)}
                errMessage={errorMessages.otp}
                autoFocus={true}
                type="number"
              />
              <TextInput
                inputName="newPassword"
                placeholder="Enter new password"
                className="form-input"
                label="New Password"
                labelClassName="form-input-label"
                value={newPassword}
                onChange={(str) => setNewPassword(str)}
                errMessage={errorMessages.newPassword}
                isPassword={true}
                rightElement={
                  <IconButton
                    onClick={() => togglePasswordVisibility("newPassword")}
                    colorScheme="transparent"
                    icon={showPassword.newPassword ? <HidePassword /> : <ShowPassword />}
                  />
                }
                type={showPassword.newPassword ? "text" : "password"}
              />

              <TextInput
                inputName="confirmPassword"
                placeholder="Re-enter new password"
                className="form-input"
                label="Confirm Password"
                labelClassName="form-input-label"
                value={confirmPassword}
                onChange={(str) => setConfirmPassword(str)}
                errMessage={errorMessages.confirmPassword}
                isPassword={true}
                rightElement={
                  <IconButton
                    onClick={() => {
                      if (arePasswordsSame) return;
                      togglePasswordVisibility("confirmPassword")
                    }}
                    colorScheme="transparent"
                    cursor={arePasswordsSame ? "default" : "pointer"}
                    icon={
                      arePasswordsSame ? (
                        <TickIconGreen />
                      ) : showPassword.confirmPassword ? (
                        <HidePassword />
                      ) : (
                        <ShowPassword />
                      )
                    }
                  />
                }
                type={showPassword.confirmPassword ? "text" : "password"}
              />
            </>
          )}
        </div>

        <div className="err-msg-cnt">
          <span className="err-msg">{ errorMessages.generalError ? errorMessages.generalError : "" }</span>
        </div>

        <div className="button-container">
          {!isOtpRequested ? (
            <Button className={`cp-btn ${!registeredNumber && "cp-btn-disabled"}`} onClick={handleGetOtp}>
              Request OTP
            </Button>
          ) : (
            <div className="verify">
              <Button className={`cp-btn ${(!otp || !newPassword || !confirmPassword) && "cp-btn-disabled"}`} onClick={handleVerifyOtpAndUpdatePassword}>
                Verify OTP & Update Password
              </Button>
              <span className="verify-help">
                {counter > 0 ? (
                  `Resend OTP in ${counter} seconds`
                ) : (
                  <>
                    Didn't receive the OTP?
                    <span className="verify-link" onClick={handleGetOtp}>
                      Resend
                    </span>
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      <PopUpModal open={showSuccessModal} modalMsg={getSuccessModalBody()} onModalClose={() => {}} />
    </div>
  );
};

export default ChangePassword;